/******************************************************************************
 *** BgtOvrlExpndRsdocMng ** 총괄 조합비 지출결의서 작성
*******************************************************************************/
import React from "react";
import AppContext from "@/js/common/AppContext";
import Axios from "@/js/common/Axios";
import Button from "@/js/common/ui/Button";
import Util from "@/js/common/Util";
import TextBox from "@/js/common/ui/input/TextBox";
import SelectBox from "@/js/common/ui/input/SelectBox";
import DatePickerBox from "@/js/common/ui/input/DatePickerBox";
import NumberTextBox from "@/js/common/ui/input/NumberTextBox";
import Modal from "@/js/common/ui/modal/Modal";
import DialogTitleBar from "@/js/system/common/DialogTitleBar";
import * as Atom from "@/js/common/Atom";
import { useRecoilValue } from "recoil";
import BudgetFile from "../budgetFileBox/BudgetFile";
import { budgetFilesProps } from "@/js/common/types/types";
import BgtOvrlGrnamtDialog from "./dialog/BgtOvrlGrnamtDialog";
import BgtOvrlEmpGrnamtDialog from "./dialog/BgtOvrlEmpGrnamtDialog";
import BgtOvrlExpndRsdocList from "./BgtOvrlExpndRsdocList";
import AtrzLineBox from "./dialog/AtrzLineBox";

const BgtOvrlExpndRsdocMng = () => {
    const userInfo = useRecoilValue(Atom.userInfo);

    // 조회조건
    const [upOgnzCd        , setUpOgnzCd        ] = React.useState<string>(userInfo.upOgnzCd); // 상위조직(본부)
    const [ognzCd          , setOgnzCd          ] = React.useState<string>(userInfo.ognzCd); // 조직(지부)
    const [acntgYear       , setAcntgYear       ] = React.useState<string>(Util.getNowAcntgYear()); // 회계연도
    const [searchAtrzSttsCd, setSearchAtrzSttsCd] = React.useState<string>(""); // 진행상태(검색)

    // 지출결의서 내용
    const [ovrlMbsdsExpndDocId, setOvrlMbsdsExpndDocId] = React.useState<string>(""); // 지출결의서 문서 ID
    const [expndRsdocNo       , setExpndRsdocNo       ] = React.useState<string>(""); // 결의서 문서번호
    const [implDt             , setImplDt             ] = React.useState<string>(""); // 행위년월일(집행일자)(YYYYMMDD)
    const [bgtSbjct1stCd      , setBgtSbjct1stCd      ] = React.useState<string>("E_002"        ); // 1차 코드(관) - 운영비
    const [bgtSbjct2stCd      , setBgtSbjct2stCd      ] = React.useState<string>("E_002_002"    ); // 2차 코드(항) - 교부금
    const [bgtSbjct3stCd      , setBgtSbjct3stCd      ] = React.useState<string>("E_002_002_001"); // 3차 코드(목) - 교부금
    const [abstrCn            , setAbstrCn            ] = React.useState<string>(""); // 적요
    const [memo               , setMemo               ] = React.useState<string>(""); // 메모
    const [implAmt            , setImplAmt            ] = React.useState<string>(""); // 집행금액
    const [atrzOpnnCn         , setAtrzOpnnCn         ] = React.useState<string>(""); // 반려사유(결재의견)
    const [atrzSttsCd         , setAtrzSttsCd         ] = React.useState<string>("00"); // 결재상태정보
    const [grnamtSeCd         , setGrnamtSeCd         ] = React.useState<string>(""); // 교부금 구분코드(교부금[01], 우정실무원[02])
    const [availAmount        , setAvailAmount        ] = React.useState<string>("0");   // 집행 가능 금액(표시)
    const [listLine           , setListLine           ] = React.useState<any[]>([]);  // 결재 라인 정보
    const [openLine           , setOpenLine           ] = React.useState<boolean>(false); // 기본 펼침/접힘


    // 파일
    const [ovrlExpndFiles     , setOvrlExpndFiles     ] = React.useState<File[]>([]); // 저장할 파일
    const [savedOvrlExpndFiles, setSavedOvrlExpndFiles] = React.useState<budgetFilesProps[]>([]); // 저장된 파일

    // 재검색 처리
    const [callTime, setCallTime] = React.useState<Date>(new Date());
    const [callTime1, setCallTime1] = React.useState<Date>(new Date()); // 문서번호 재조회용

    // 모달 오픈
    const [grnamtModalOpen   , setGrnamtModalOpen   ] = React.useState<boolean>(false); // 교부금 모달
    const [empGrnamtModalOpen, setEmpGrnamtModalOpen] = React.useState<boolean>(false); // 우정실무원 교부금 모달

    // 상세목록 교부금, 우정실무원교부금 합계
    const [grnamtSum   , setGrnamtSum   ] = React.useState<string>("0"); // 교부금 합계
    const [empGrnamtSum, setEmpGrnamtSum] = React.useState<string>("0"); // 우정실무원 교부금 합계

    // 총괄 조합비 지출결의서 상세내역 조회
    React.useEffect(() => {
        AppContext.showSpinner();
        Axios.dataAccess({
            url: "system/bgt/ovrl/expnd/select-ovrl-expnd-rsdoc-dtl.do",
            methodType: "post",
            paramType: "json",
            paramData: {
                ovrlMbsdsExpndDocId: ovrlMbsdsExpndDocId
            },
            onSuccessFn: (res: any) => {
                if (Util.isNotEmpty(res.result.item)) {
                    const item = res.result.item;
                    setOvrlMbsdsExpndDocId(item.ovrlMbsdsExpndDocId); // 지출결의서 문서 ID
                    setExpndRsdocNo       (item.expndRsdocNo       ); // 지출결의서 문서 번호
                    setImplDt             (item.implDt             ); // 행위년월일(집행일자)
                    setBgtSbjct1stCd      (item.bgtSbjct1stCd      ); // 1차 코드(관)
                    setBgtSbjct2stCd      (item.bgtSbjct2stCd      ); // 2차 코드(항)
                    setBgtSbjct3stCd      (item.bgtSbjct3stCd      ); // 3차 코드(목)
                    setImplAmt            (item.implAmt            ); // 집행금액
                    setAbstrCn            (item.abstrCn            ); // 적요
                    setMemo               (item.memo               ); // 메모
                    setAtrzOpnnCn         (item.atrzOpnnCn         ); // 반려사유(결재의견)
                    setAtrzSttsCd         (item.atrzSttsCd         ); // 결재상태정보
                    setGrnamtSeCd         (item.grnamtSeCd         ); // 교부금 구분
                    setListLine           (res.result.listLine);  // 결재 라인리스트

                    // 첨부파일
                    setSavedOvrlExpndFiles(res.result.listFile);

                    setGrnamtSum   (res.result.listGrnamtSum.sumMbsdsSumAmt); // 교부금 조합비 합계
                    setEmpGrnamtSum(res.result.listEmpGrnamtSum.mbsdsSumAmt); // 우정실무원 교부금 조합비 합계

                    // 집행가능 금액 조회
                    Axios.dataAccess({
                        url: "system/bgt/ovrl/expnd/select-ovrl-incm-mbsds.do",
                        methodType: "post",
                        paramType: "json",
                        paramData: {
                            orgCd         : item.orgCd                    , // 조직코드
                            acntgYear     : Util.getAcntgYear(item.implDt), // 회계연도
                            grnamtSeCd    : item.grnamtSeCd               , // 교부금 구분
                            bgtSbjct3stCd : item.bgtSbjct3stCd            , // 목 코드
                        },
                        onSuccessFn: (res: any) => {
                            if (Util.isNotEmpty(res.result.item)) {
                                const item = res.result.item;
                                setAvailAmount(item.sumDpstAmt); // 집행가능금액
                            }
                            AppContext.hideSpinner();
                        }
                    });
                }
                AppContext.hideSpinner();
            }
        });
    }, [ovrlMbsdsExpndDocId, callTime1]);

    // 입력 폼 초기화
    function doReset() {
        setOvrlMbsdsExpndDocId("");
        setExpndRsdocNo       ("");
        setImplDt             (""); // 행위년월일 (YYYYMMDD)
        setBgtSbjct1stCd      ("E_002"        ); // 1차 코드(관)
        setBgtSbjct2stCd      ("E_002_002"    ); // 2차 코드(항)
        setBgtSbjct3stCd      ("E_002_002_001"); // 3차 코드(목)
        setAbstrCn            (""); // 적요
        setMemo               (""); // 메모
        setImplAmt            (""); // 집행금액
        setAtrzOpnnCn         (""); // 반려사유
        setAtrzSttsCd         ("00"); // 상태정보
        setGrnamtSeCd         (""); // 교부금 구분 코드
        setGrnamtSum          ("0"); // 교부금 조합비 합계
        setEmpGrnamtSum       ("0"); // 우정실무원 교부금 조합비 합계
        setAvailAmount        ("0");

        setOvrlExpndFiles     ([]); // 저장할 파일
        setSavedOvrlExpndFiles([]); // 저장된 파일
    };

    // 문서 ID 변경 시 드롭존 초기화
    React.useEffect(() => {
        setOvrlExpndFiles([]);
    }, [ovrlMbsdsExpndDocId]);

    // 필수 입력사항 체크
    function checkValue() {
        if (Util.isEmpty(implDt        )) { AppContext.openAlert({ title: "행위년월일 확인" , msg: "행위년월일을 선택하세요."      , icon: "warning" }); return false; }
        if (Util.isEmpty(bgtSbjct1stCd )) { AppContext.openAlert({ title: "예산과목 관 확인", msg: "예산과목 관 항목을 선택하세요.", icon: "warning" }); return false; }
        if (Util.isEmpty(bgtSbjct2stCd )) { AppContext.openAlert({ title: "예산과목 항 확인", msg: "예산과목 항 항목을 선택하세요.", icon: "warning" }); return false; }
        if (Util.isEmpty(bgtSbjct3stCd )) { AppContext.openAlert({ title: "예산과목 목 확인", msg: "예산과목 목 항목을 선택하세요.", icon: "warning" }); return false; }
        return true;
    }

    function doSave(atrzSttsCd: string, grnamtSeCd?: string) {
        let msgText = "임시저장";
        let cfmMsg  = "";

        // 교부금 or 우정실무원 교부금 버튼 클릭 - 지출결의서 Insert 후 교부금 모달 오픈
        if (atrzSttsCd === "00") {
            if (Util.isEmpty(grnamtSeCd)) {{
                AppContext.openAlert({
                    title: "교부금 구분 확인",
                    msg: "교부금 구분을 선택하세요.",
                    icon: "warning"
                });
                return;
            }}
            if (Util.isEmpty(ovrlMbsdsExpndDocId)) {
                cfmMsg = "지출결의서가 저장되지 않았습니다.\n"
            } else {
                if (grnamtSeCd === "01") {          // 교부금
                    setGrnamtModalOpen(true);
                } else if (grnamtSeCd === "02") {   // 우정실무원 교부금
                    setEmpGrnamtModalOpen(true);
                }
                return;
            }
        }

        // 필수입력사항 체크
        const isValidate = checkValue();
        if (!isValidate) return;

        // 작성완료 필수입력 확인
        if (atrzSttsCd === "02") {
            if (Util.isEmpty(implAmt) || implAmt === "0") { AppContext.openAlert({ title: "금액 확인", msg: "금액을 입력하세요.", icon: "warning" }); return; }
            if (Number(Util.uncomma(availAmount)) - Number(Util.uncomma(implAmt)) < 0) {
                AppContext.openAlert({ title: "집행금액 확인",
                    msg: "집행금액[" + Util.comma(implAmt) + "]이 " + "집행가능금액[" + Util.comma(availAmount) + "]보다 큽니다.", icon: "warning" }); return;
            }

            if (Util.isEmpty(abstrCn)) { AppContext.openAlert({ title: "적요 확인", msg: "적요를 입력하세요.", icon: "warning" }); return; }


            msgText = "작성완료";
        }

       AppContext.openAlert({
            title: "지출결의서 확인",
            msg: cfmMsg + "지출결의서를 " + msgText + "하시겠습니까?",
            icon: "question",
            closeText: "취소",
            confirmText: msgText,
            handleConfirm: () => {
                AppContext.showSpinner();

                Axios.dataAccess({
                url: "system/bgt/ovrl/expnd/save-ovrl-expnd-rsdoc.do",
                methodType: "post",
                isMultipart: true,
                paramData: {
                    ovrlMbsdsExpndDocId: ovrlMbsdsExpndDocId,
                    orgCd              : ognzCd               , // 조직코드
                    implDt             : implDt               , // 행위년월일 (YYYYMMDD)
                    acntgYear          : Util.getAcntgYear(implDt), // 회계연도
                    bgtSbjct1stCd      : bgtSbjct1stCd        , // 1차 코드(관)
                    bgtSbjct2stCd      : bgtSbjct2stCd        , // 2차 코드(항)
                    bgtSbjct3stCd      : bgtSbjct3stCd        , // 3차 코드(목)
                    abstrCn            : abstrCn              , // 적요
                    memo               : memo                 , // 메모
                    implAmt            : Util.uncomma(implAmt) === "" ? 0 : Number(Util.uncomma(implAmt)), // 집행금액
                    atrzSttsCd         : atrzSttsCd === "00" ? "01" : atrzSttsCd, // 상태정보
                    grnamtSeCd         : grnamtSeCd    , // 교부금 구분코드
                    budgetFiles        : ovrlExpndFiles, // 첨부파일
                    fileSeCd           : "C02"           // 파일구분코드(총괄조합비_지출결의서[C02])
                },
                    onSuccessFn: (res: any) => {
                        AppContext.openAlert({
                            title: "지출결의서 저장",
                            msg: "지출결의서가 저장되었습니다.",
                            icon: "check",
                        });
                        // 지출결의서 Insert 후 문서 ID 설정
                        if (Util.isEmpty(ovrlMbsdsExpndDocId)) {
                            setOvrlMbsdsExpndDocId(res.result.ovrlMbsdsExpndDocId); // 지출결의서 문서 ID
                            if (atrzSttsCd === "00") {
                                if (grnamtSeCd === "01") {
                                    setGrnamtModalOpen(true);
                                } else if (grnamtSeCd === "02") {
                                    setEmpGrnamtModalOpen(true);
                                }
                            }
                        } else if(atrzSttsCd === "01") {
                            setCallTime1(new Date());
                        }
                        if (atrzSttsCd === "02") {
                            doReset(); // 작성완료인 경우 전체 초기화 처리
                        }
                        setCallTime(new Date());
                    },
                });
            },
        });
    };

    // 삭제
    function doDelete() {
        if (Util.isEmpty(ovrlMbsdsExpndDocId)) return;

        AppContext.openAlert({
            title: "삭제",
            msg: "지출결의서를 삭제하시겠습니까?",
            icon: "check",
            confirmText: "삭제",
            handleConfirm: () => {
                AppContext.showSpinner();
                Axios.dataAccess({
                    url: "system/bgt/ovrl/expnd/delete-ovrl-expnd-rsdoc.do",
                    methodType: "post",
                    paramType: "json",
                    paramData: {
                        ovrlMbsdsExpndDocId: ovrlMbsdsExpndDocId, // 지출결의서 문서 ID
                        grnamtSeCd         : grnamtSeCd         , // 교부금 구분코드
                    },
                    onSuccessFn: () => {
                        AppContext.openAlert({
                            title: "지출결의서 삭제 완료",
                            msg: "지출결의서가 삭제 완료되었습니다.",
                            icon: "warning",
                        });
                        AppContext.hideSpinner();
                        doReset(); // 초기화
                        setCallTime(new Date());
                    },
                });
            },
        });
    };

    // 집행가능금액 확인
    function getAvailAmount(grnamtSeCd: string) {
        // 교부금 구분에 따른 집행가능 금액 확인
        // 01. 교부금: 조합비 > 의무금 > 조합비
        // 02. 우정실무원 교부금: 조합비 > 의무금 > 무기계약직 조합비
        AppContext.showSpinner();
        Axios.dataAccess({
            url: "system/bgt/ovrl/expnd/select-ovrl-incm-mbsds.do",
            methodType: "post",
            paramType: "json",
            paramData: {
                orgCd     : ognzCd                   , // 조직코드
                acntgYear : Util.getAcntgYear(implDt), // 회계연도
                grnamtSeCd: grnamtSeCd               , // 교부금 구분
                bgtSbjct3stCd: bgtSbjct3stCd         , // 목 코드
            },
            onSuccessFn: (res: any) => {
                if (Util.isNotEmpty(res.result.item)) {
                    const item = res.result.item;
                    setAvailAmount(item.sumDpstAmt); // 집행가능금액
                }
                AppContext.hideSpinner();
            }
        });
    }

    return (
        <div className="w1200 bgcWhite p20 bdrd8 mt10 boxShadowSoft">
            <div className="mt5">
                <span className="dpBlock w100p fm-c fs25 fc2c3e50 ls1 pb10">교부금 지출결의서 관리</span>
                <div className="bdt3 bdc007bff pb10" />
            </div>

            {/* 검색 영역 */}
            <div className="bd1 bdcDDDDDD p20 mb10 bdrd6 bgcFAFAFA">
                <div className="dpFlx aiC flxWrap gap14">
                    {/* 소속 */}
                    <div className="dpFlx aiC">
                        <span className="w50 fm-c fs15 fc333333 ls1 taL">본부</span>
                        <SelectBox value={upOgnzCd}
                            width={150} height={36} fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                            borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                            background="#f6f6f6" paddingRight={8}
                            codeArray={Util.getUpOrgCodeArray("01")}
                            placeholder="전체"
                            onChangeFunc={(data: string) => setUpOgnzCd(data)}
                            disabled={true}
                        />
                    </div>

                    {/* 지부 */}
                    <div className="dpFlx aiC">
                        <span className="w50 fm-c fs15 fc333333 ls1 taL">지부</span>
                        <SelectBox value={ognzCd}
                            width={180} height={36}
                            fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                            borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                            background="#F6F6F6" paddingRight={8}
                            codeArray={Util.getDownOrgCodeArray(upOgnzCd)}
                            placeholder="전체"
                            onChangeFunc={(data: string) => setOgnzCd(data)}
                            disabled={true}
                        />
                    </div>

                    {/* 회계연도 */}
                    <div className="dpFlx aiC">
                        <span className="w80 fm-c fs15 fc333333 ls1 taL">회계연도</span>
                        <SelectBox value={acntgYear}
                            width={120} height={36}
                            fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                            borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                            background="#fff" paddingRight={8}
                            codeArray={Util.getAcntgYearArray()}
                            placeholder="선택"
                            onChangeFunc={(data: string) => setAcntgYear(data)}
                        />
                    </div>

                    {/* 진행상태 */}
                    <div className="dpFlx aiC">
                        <span className="w80 fm-c fs15 fc333333 ls1 taL">진행상태</span>
                        <SelectBox  value={searchAtrzSttsCd}
                            width={140} height={36}
                            fontSize={14}
                            fontFamily="fm-c" color="#333" letterSpacing={-1}
                            borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                            background="#fff"
                            codeArray={[{ code: "", name: "전체" }].concat(Util.getCodeArray("UI_ATRZ_STTS_CD_01"))}
                            placeholder="전체" paddingRight={8}
                            onChangeFunc={(data: string) => setSearchAtrzSttsCd(data)}
                        />
                    </div>
                </div>
            </div>

            <div className="dpFlx gap14 aiFs">
                {/* 좌측 입력 */}
                <div className="w360">
                    <div className="bd1d9d9d9 bdrd6 bgcWhite p15">
                        <div className="mb10">
                            <span className="fm-b fs16 fc007bff ls1 fw700">지출결의서 입력</span>
                            <span className="fm-b fs13 ls06 fc1A5276" style={{marginLeft: "66px"}}>{expndRsdocNo}</span>
                        </div>

                        {atrzSttsCd === "05" && (
                        <AtrzLineBox
                            listLine={listLine}
                            open={openLine}
                            onToggle={() => setOpenLine((v) => !v)}
                        />)}

                        {/* 행위년월일 */}
                        <div className="dpFlx flxdCol mb10">
                            <div className="h30 dpFlx aiE pb5">
                                <span className="fm-b fs13 ls06 fc111111">행위년월일</span>
                            </div>
                            <DatePickerBox value={implDt}
                                width={320} height={36} fontSize={14}
                                fontFamily="fm-c" color="#333" letterSpacing={-1}
                                borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                                bgColor={["01", "02", "05"].includes(atrzSttsCd) && userInfo.ognzCd !== "00_000" ? "#F6F6F6": "white"}
                                textAlign="left"
                                calendarThemeColor={AppContext.themeColor}
                                disabled={["01", "02", "05"].includes(atrzSttsCd) && userInfo.ognzCd !== "00_000"}   // 작성중(01), 작성완료(02), 반려(05): 임시저장 이후 수정불가
                                onChange={(data: string) => setImplDt(data)}
                            />
                        </div>

                        {/* 관/항/목 */}
                        <div className="dpFlx mb10">
                            {/* 관 */}
                            <div className="dpFlx flxdCol flx1">
                                <div className="h30 dpFlx aiE pb5">
                                    <span className="fm-b fs13 ls06 fc111111">관</span>
                                </div>
                                <SelectBox value={bgtSbjct1stCd}
                                    width={100} height={36}
                                    fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                                    borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                                    background="#F0F0F0" paddingRight={8}
                                    codeArray={[{ code: "", name: "선택" }].concat(Util.getBgt1CodeArray("02", ognzCd))}
                                    placeholder="선택"
                                    onChangeFunc={(data: string) => {
                                        setBgtSbjct1stCd(data);
                                        setBgtSbjct2stCd("");
                                        setBgtSbjct3stCd("");
                                    }}
                                    disabled={true}
                                />
                            </div>


                            {/* 항 */}
                            <div className="dpFlx flxdCol flx1">
                                <div className="h30 dpFlx aiE pb5">
                                    <span className="fm-b fs13 ls06 fc111111">항</span>
                                </div>
                                <SelectBox value={bgtSbjct2stCd}
                                    width={100} height={36}
                                    fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                                    borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                                    background="#F0F0F0" paddingRight={8}
                                    codeArray={[{ code: "", name: "선택" }].concat(Util.getBgt2CodeArray(bgtSbjct1stCd, ognzCd))}
                                    placeholder="선택"
                                    onChangeFunc={(data: string) => {
                                        setBgtSbjct2stCd(data);
                                        setBgtSbjct3stCd("");
                                    }}
                                    disabled={true}
                                />
                            </div>

                            {/* 목 */}
                            <div className="dpFlx flxdCol flx1">
                                <div className="h30 dpFlx aiE pb5">
                                    <span className="fm-b fs13 ls06 fc111111">목</span>
                                </div>
                                <SelectBox  value={bgtSbjct3stCd}
                                    width={100} height={36}
                                    fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                                    borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                                    background="#F0F0F0" paddingRight={8}
                                    codeArray={[{ code: "", name: "선택" }].concat(Util.getBgt3CodeArray(bgtSbjct2stCd, ognzCd))}
                                    placeholder="선택"
                                    onChangeFunc={(data: string) => setBgtSbjct3stCd(data)}
                                    disabled={true}
                                />
                            </div>
                        </div>

                        {/* 금액 + 집행 가능 금액 */}
                        <div className="dpFlx gap10 aiE mb10">
                            <div className="flx1">
                                <div className="h30 dpFlx aiE pb5">
                                    <span className="fm-b fs13 ls06 fc111111">금액 (원)</span>
                                </div>
                                <div className="w150 h36 bgcWhite dpFlx aiC bsBdb">
                                    <NumberTextBox value={implAmt}
                                        className="bgcF0F0F0 w100p h36 bd1 bdrd2"
                                        thousandsGroupStyle="thousand" thousandSeparator=","
                                        placeholder="0"
                                        fontSize={14} fontFamily="fm-c" letterSpacing={-1}
                                        onChange={(data: string) => setImplAmt(data)}
                                        inputClass={"taR mr10"}
                                        color="#007bff"
                                        disabled={true}
                                    />
                                </div>
                            </div>

                            <div className="flx1">
                                <div className="h30 dpFlx aiE pb5">
                                    <span className="fm-b fs13 ls06 fc111111">집행가능 금액</span>
                                </div>
                                <div className="w150 h36 bgcF0F0F0 dpFlx aiC bsBdb">
                                    <NumberTextBox  value={availAmount}
                                        className="w100p h100p bd1 bdrd2 bgcF0F0F0"
                                        thousandsGroupStyle="thousand" thousandSeparator=","
                                        placeholder="0"
                                        fontSize={14} fontFamily="fm-c" letterSpacing={-1} color="#333"
                                        inputClass={"taR mr10"}
                                        disabled={true}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* 적요 */}
                        <div className="dpFlx flxdCol mb10">
                            <div className="h30 dpFlx aiE pb5">
                                <span className="fm-b fs13 ls06 fc111111">적요</span>
                            </div>
                            <TextBox value={abstrCn}
                                width={320} height={36} fontSize={14}
                                fontFamily="fm-c" color="#333" letterSpacing={-1}
                                borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                                style={{ backgroundColor: "#fff", padding: "0 12px" }}
                                placeholder="예) 12월 소모품 구입"
                                onChangeFunc={(text: string) => setAbstrCn(text)}
                            />
                        </div>

                        <div className="dpFlx flxdCol mb10">
                            <div className="h30 dpFlx aiE pb5">
                                <span className="fm-b fs13 ls06 fc111111">메모</span>
                            </div>
                            <TextBox value={memo}
                                width={320} height={36} fontSize={14}
                                fontFamily="fm-c" color="#333" letterSpacing={-1}
                                borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                                style={{ backgroundColor: "#fff", padding: "0 12px" }}
                                placeholder="메모를 입력하세요."
                                onChangeFunc={(text: string) => setMemo(text)}
                            />
                        </div>

                        {/* 반려 */}
                        {atrzSttsCd === "05" && (
                            <div className="dpFlx flxdCol">
                                <div className="h30 dpFlx aiE pb5">
                                    <span className="fm-b fs13 ls06 fc111111">반려 사유</span>
                                </div>
                                <div className="dpFlx aiFs">
                                    <div className="flxg1 bsBdb">
                                        <textarea
                                            value={atrzOpnnCn}
                                            className="w320 h70 bsBdb p10 bd1 bdcDFDFDF bdrd2 fm-c fs14 fc333333 ls1 bgcWhite rszN"
                                            maxLength={2000}
                                            placeholder=""
                                            onChange={(e) => setAtrzOpnnCn(e.target.value)}
                                            readOnly={true}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* 첨부파일 */}
                        <BudgetFile
                            atrzSttsCd          = {atrzSttsCd}  // 진행상태
                            fileSeCd            = "C02" // 총괄 조합비 지출결의서(C02)
                            savedBudgetFiles    = {savedOvrlExpndFiles}
                            setSavedBudgetFiles = {setSavedOvrlExpndFiles}
                            budgetFiles         = {ovrlExpndFiles}
                            setBudgetFiles      = {setOvrlExpndFiles}
                        />

                        {/* 교부금 구분 */}
                        <div className="dpFlx flxdCol mb10">
                            <div className="h30 dpFlx aiE pb5">
                                <span className="fm-b fs13 ls06 fc111111">교부금 구분</span>
                            </div>
                            <div className="w320 h36 bd1d9d9d9 dpFlx aiC bsBdb" style={{backgroundColor: ["01", "02", "05"].includes(atrzSttsCd) && Util.isNotEmpty(grnamtSeCd) ? "#F6F6F6": "white"}}>
                                <div className="ml10">
                                    <label className="dpFlx csP">
                                        <input type="radio" value={"01"} checked={grnamtSeCd === "01"}
                                            onChange={(e) => {
                                                // 행위년월일 확인
                                                if (Util.isEmpty(implDt)) {
                                                    AppContext.openAlert({
                                                        title: "행위년월일 확인",
                                                        msg: "행위년월일을 선택하세요.",
                                                        icon: "warning"
                                                    });
                                                    return;
                                                }
                                                getAvailAmount("01");
                                                setGrnamtSeCd(e.target.value);
                                            }}
                                            style={{transform: "scale(1.2)"}}
                                            disabled={["01", "02", "05"].includes(atrzSttsCd) && Util.isNotEmpty(grnamtSeCd) ? true : false}   // 작성중(01), 작성완료(02), 반려(05): 임시저장 이후 수정불가
                                        />
                                        <div className="mb2">
                                            <span className="fm-b c333333 ls1 fs14 ml3" style={{fontWeight: grnamtSeCd === "01" ? "700" : ""}}>교부금</span>
                                        </div>
                                    </label>
                                </div>
                                <div className="ml10 ml20 aiC">
                                    <label className="dpFlx csP">
                                        <input type="radio" value={"02"} checked={grnamtSeCd === "02"}
                                            onChange={(e) => {
                                                // 행위년월일 확인
                                                if (Util.isEmpty(implDt)) {
                                                    AppContext.openAlert({
                                                        title: "행위년월일 확인",
                                                        msg: "행위년월일을 선택하세요.",
                                                        icon: "warning"
                                                    });
                                                    return;
                                                }
                                                getAvailAmount("02");
                                                setGrnamtSeCd(e.target.value);
                                            }}
                                            style={{transform: "scale(1.2)"}}
                                            disabled={["01", "02", "05"].includes(atrzSttsCd) && Util.isNotEmpty(grnamtSeCd) ? true : false}   // 작성중(01), 작성완료(02), 반려(05): 임시저장 이후 수정불가
                                        />
                                        <div className="mb2">
                                            <span className="fm-b c333333 ls1 fs14 ml3" style={{fontWeight: grnamtSeCd === "02" ? "700" : ""}}>우정실무원 교부금</span>
                                        </div>
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* 교부금 등록 */}
                        <div className="dpFlx gap10 aiE mb10">
                            {/* 금액 */}
                            <div className="flx1">
                                <div className="w200 h36 aiC bsBdb bd1 bdcDDDDDD bdrd3 bgcF0F0F0 taR pt2">
                                    <span className="fm-c ls1 fs14 fc333333 pr10">{grnamtSeCd === "01" ? Util.comma(grnamtSum) : Util.comma(empGrnamtSum)}</span>
                                </div>
                            </div>

                            {/* 교부금 등록 */}
                            <div className="flx1">
                                <div className="w155 h36 dpFlx aiC bsBdb">
                                    <Button buttonName="교부금 등록"
                                        width={110} height={35}
                                        fontFamily="fm-b" fontSize={14} letterSpacing={-1} color="#fff"
                                        backgroundColor="#009999" borderRadius={6}
                                        buttonClass="dpFlx aiC jcC bd1 bdcForest"
                                        onClickFunc={() => doSave("00", grnamtSeCd)}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="bdt1 bdcDDDDDD mt15 mb15" />

                        <div className="dpFlx aiC jcSb gap10">
                            <Button buttonName="초기화"
                                width={80} height={40}
                                fontFamily="fm-b" fontSize={16} letterSpacing={-1}
                                variant="styInitBtn" borderRadius={6}
                                buttonClass="dpFlx aiC jcC"
                                onClickFunc={() => doReset()}
                            />

                            {(atrzSttsCd === "01" || atrzSttsCd === "02" || atrzSttsCd === "05") &&
                                <Button buttonName="삭제"
                                    width={75} height={40}
                                    fontFamily="fm-b" fontSize={15} letterSpacing={-1}
                                    variant="styDeleteBtn" borderRadius={6}
                                    buttonClass="dpFlx aiC jcC"
                                    onClickFunc={doDelete}
                                />
                            }

                            {(atrzSttsCd === "00" || atrzSttsCd === "01") &&
                                <Button buttonName="임시저장"
                                    width={100} height={40}
                                    fontFamily="fm-b" fontSize={16} letterSpacing={-1}
                                    variant="styTempBtn" borderRadius={6}
                                    buttonClass="dpFlx aiC jcC"
                                    onClickFunc={() => doSave("01")}
                                />
                            }

                            <Button buttonName="작성완료"
                                width={100} height={40}
                                fontFamily="fm-b" fontSize={16} letterSpacing={-1}
                                variant="styCompleteBtn" borderRadius={6}
                                buttonClass="dpFlx aiC jcC"
                                onClickFunc={() => doSave("02")}
                            />
                        </div>
                    </div>
                </div>

                {/* 우측 지출결의서 목록 */}
                <div className="flx1">
                    <BgtOvrlExpndRsdocList
                        ognzCd                 = {ognzCd    } // 조직코드
                        acntgYear              = {acntgYear } // 회계연도
                        searchAtrzSttsCd       = {searchAtrzSttsCd} // 상태정보(검색)
                        setOvrlMbsdsExpndDocId = {setOvrlMbsdsExpndDocId} // 지출결의서 문서 ID Setting
                        callTime               = {callTime}
                    ></BgtOvrlExpndRsdocList>
                </div>
            </div>


            {/*  교부금 모달 */}
            <Modal modalOpen={grnamtModalOpen} dim={true} visible={true}
                titleEl={<DialogTitleBar title="교부금 배정 관리" />}
                onCloseFunc={() => setGrnamtModalOpen(false)}
            >
                <BgtOvrlGrnamtDialog
                    ovrlMbsdsExpndDocId = {ovrlMbsdsExpndDocId} // 지출결의서 문서 ID
                    setGrnamtModalOpen  = {setGrnamtModalOpen}  // 교부금 모달
                    setGrnamtSum        = {setGrnamtSum}        // 교부금 합계
                    setImplAmt          = {setImplAmt}          // 집행금액
                />
            </Modal>


            {/* 우정실무원 교부금 모달 */}
            <Modal modalOpen={empGrnamtModalOpen} dim={true} visible={true}
                titleEl={<DialogTitleBar title="우정 실무원 교부금 배정 관리"/>}
                onCloseFunc={() => setEmpGrnamtModalOpen(false)}
            >
                <BgtOvrlEmpGrnamtDialog
                    ovrlMbsdsExpndDocId   = {ovrlMbsdsExpndDocId}
                    setEmpGrnamtModalOpen = {setEmpGrnamtModalOpen}
                    setEmpGrnamtSum       = {setEmpGrnamtSum}   // 우정실무원 교부금 합계
                    setImplAmt            = {setImplAmt}        // 집행금액
                />
            </Modal>
        </div>
    );
};

export default BgtOvrlExpndRsdocMng;